import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useIepComparison } from '../hooks/use-iep-comparison';
import { ComparisonSummary } from './comparison-summary';
import { GoalDiffCard } from './goal-diff-card';
import { SectionDiff } from './section-diff';
import { RedFlagResolution } from './red-flag-resolution';
import { Card } from '@/components/ui/card';
import { Notice } from '@/components/ui/notice';
import { Spinner } from '@/components/ui/spinner';
import { PageLayout } from '@/components/ui/page-layout';
import { getActiveLanguage } from '@/lib/i18n/format';

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString(getActiveLanguage(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function ComparisonView({
  iepId,
  otherId,
  childId,
}: {
  iepId: number;
  otherId: number;
  childId: number;
}) {
  const { t } = useTranslation('iep-comparison');
  const { comparison, isLoading, error } = useIepComparison(iepId, otherId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t('loading')} />
      </div>
    );
  }

  if (error || !comparison) {
    return (
      <div className="space-y-4">
        <Link
          to={`/children/${childId}`}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-slate-500 hover:text-brand-teal-500 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.8} />
          {t('backToChild')}
        </Link>
        <Notice variant="error" title={t('failedTitle')}>
          {error || t('failedGeneric')}
        </Notice>
      </div>
    );
  }

  const { goalChanges, sectionChanges, redFlagResolution, summary } = comparison;
  const hasGoalChanges =
    goalChanges.added.length > 0 ||
    goalChanges.removed.length > 0 ||
    goalChanges.modified.length > 0;

  return (
    <PageLayout
      title={t('title')}
      className="max-w-5xl"
      breadcrumb={[
        { label: t('backToChild'), to: `/children/${childId}` },
        { label: t('comparisonBreadcrumb') },
      ]}
    >
      {/* Date range */}
      <div className="flex items-center gap-2 text-[15px] text-brand-slate-600">
        <span className="font-medium">{formatDate(comparison.olderDate)}</span>
        <ArrowRight className="w-4 h-4 text-brand-slate-400" strokeWidth={1.8} />
        <span className="font-medium">{formatDate(comparison.newerDate)}</span>
      </div>

      {/* Summary stats */}
      <ComparisonSummary summary={summary} />

      {/* Goal changes */}
      {hasGoalChanges ? (
        <Card>
          <h3 className="font-serif text-[17px] font-semibold text-brand-slate-800 mb-4">
            {t('goalChanges.heading')}
          </h3>
          <div className="space-y-3">
            {goalChanges.added.map((goal, i) => (
              <GoalDiffCard key={`added-${i}`} type="added" goal={goal} />
            ))}
            {goalChanges.removed.map((goal, i) => (
              <GoalDiffCard key={`removed-${i}`} type="removed" goal={goal} />
            ))}
            {goalChanges.modified.map((goal, i) => (
              <GoalDiffCard key={`modified-${i}`} type="modified" goal={goal} />
            ))}
          </div>
        </Card>
      ) : (
        <Card>
          <h3 className="font-serif text-[17px] font-semibold text-brand-slate-800 mb-2">
            {t('goalChanges.heading')}
          </h3>
          <p className="text-[13px] text-brand-slate-500">{t('goalChanges.empty')}</p>
        </Card>
      )}

      {/* Section changes */}
      <SectionDiff changes={sectionChanges} />

      {/* Red flag resolution */}
      <RedFlagResolution resolution={redFlagResolution} />
    </PageLayout>
  );
}
